import type { ApiSchemas } from '$lib/api/shopwareApi.js';
import type { ShopwareOption } from '$lib/api/types/additionalShopwareTypes';
import type { PropertiesContext } from '$lib/context';
import type { Selected } from 'bits-ui';

export function createProductTitle(product: ApiSchemas['Product']): string {
    return [product?.name ?? product?.translated?.name ?? ''].filter(Boolean).join(' - ');
}

export function extractVariantOptions(
    variants: ApiSchemas['Product'][],
    currentVariant?: ApiSchemas['Product']
): { [id: string]: { name: string; selected: string; options: [{ id: string; name: string }] } } | undefined {
    if (!variants) {
        return;
    }

    const grouped: { [id: string]: { name: string; selected: string; options: [{ id: string; name: string }] } } = {};

    variants.forEach((variant) => {
        variant.options.forEach((option: ShopwareOption) => {
            const key = option.groupId;
            const groupName = option.group.translated.name;
            const optionName = option.translated.name;

            if (!grouped[key]) {
                grouped[key] = {
                    name: groupName,
                    selected: '',
                    options: [] as unknown as [{ id: string; name: string }]
                };
            }

            if (!grouped[key].options.some((optionToCheck) => optionToCheck.name === optionName)) {
                grouped[key].options.push({
                    id: option.id,
                    name: optionName
                });

                if (currentVariant) {
                    if (
                        currentVariant.options.some((productOption: ShopwareOption) => productOption.id === option.id)
                    ) {
                        grouped[key].selected = option.id;
                    }
                } else {
                    grouped[key].selected = grouped[key].options[0].id;
                }
            }
        });
    });

    return grouped;
}

export function extractLine(product: ApiSchemas['Product'], line: PropertiesContext['line'], isoCode?: string) {
    const productLine = product.properties.find(
        (property: ApiSchemas['PropertyGroupOption']) => property.groupId === line.id
    ) as ApiSchemas['PropertyGroupOption'] | undefined;

    return line.options?.[productLine?.id]?.[isoCode ?? 'en-GB'].toLowerCase();
}

// New Tech
export type GroupedOptions = { selected: Selected<string>; options: Selected<string>[] };
export type MappedVariant = { id: string; productNumber: string };

export function createVariantMap(variants: ApiSchemas['Product'][]) {
    const variantMap: Record<string, MappedVariant> = {};

    for (const variant of variants) {
        const key = createKey(variant.optionIds);
        variantMap[key] = { id: variant.id, productNumber: variant.productNumber };
    }

    return variantMap;
}

export function createKey(params: string[]) {
    return params.sort().join('-');
}

export function getUniqueOptionIds(variants: ApiSchemas['Product'][], groupId: string) {
    return [
        ...new Set(
            variants.map(
                (variant) =>
                    variant.options.find((option: ApiSchemas['Product']['options']) => option?.groupId === groupId).id
            )
        ).values()
    ];
}

export function getUniqueOptions(variants: ApiSchemas['Product'][], groupId: string) {
    return [
        ...new Set(
            variants.map((variant) =>
                variant.options.find((option: ApiSchemas['Product']['options']) => option?.groupId === groupId)
            )
        ).values()
    ];
}
